import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paiementsApi, remboursementsApi, type Paiement, type Remboursement } from '../services/api';
import { useToast } from '../context/ToastContext';
import { Spinner } from '../components/shared/Spinner';
import { PdfPreviewDrawer } from '../components/paiements/PdfPreviewDrawer';
import { MotifEtReponse } from '../components/paiements/MotifEtReponse';
import { formatApiError } from '../lib/api-error';

/**
 * Module Paiements — écran « À valider ».
 *
 * TOUT ADMINISTRATEUR y voit TOUTES les demandes et peut statuer sur celles des
 * autres (règle de Valérie du 14/10/2026 : les trois dirigeants se suppléent
 * pour qu'une absence ou une urgence ne bloque pas un paiement). Le valideur
 * initialement attribué reçoit alors un courriel qui le lui dit.
 *
 * Un valideur qui n'est PAS administrateur ne voit que ses demandes, et le
 * serveur refuse de le laisser agir sur celle d'un autre. Le filtre est fait
 * côté serveur dans les deux cas.
 * Affiche les paiements fournisseurs ET les remboursements d'avoirs soumis,
 * en deux sections. Pour chaque ligne (ou en masse par bloc / globalement) :
 * VALIDER (→ validé) ou REFUSER (→ repasse en « À payer » + motif + notif admin).
 * Quand tout est traité, une popup propose de confirmer et notifier l'admin.
 */

type Kind = 'paiement' | 'remboursement';

function eur(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtIban(iban?: string | null): string {
  return iban ? iban.replace(/(.{4})/g, '$1 ').trim() : '-';
}
const key = (kind: Kind, id: string) => `${kind}:${id}`;

function BadgeValideur({ nom }: { nom: string }) {
  return (
    <span className="ml-2 rounded bg-[#A6D3F9]/40 px-1.5 py-0.5 text-xs font-normal text-[#384872]">
      Valideur : {nom}
    </span>
  );
}

/** Case à cocher à 3 états : cochée / vide / indéterminée (sélection partielle). */
function TriCheckbox({
  checked,
  indeterminate,
  onChange,
  title,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  title?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      title={title}
      className="cursor-pointer"
    />
  );
}

export function PaiementsValidationPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Cible de refus en attente de motif (ouvre la modale). null = fermée.
  const [rejectTarget, setRejectTarget] = useState<Array<{ kind: Kind; id: string }> | null>(null);
  const [motif, setMotif] = useState('');
  // Suivi pour la popup de fin : a-t-on traité au moins une ligne, et l'a-t-on
  // déjà confirmée/reportée pour ce lot ?
  const [acted, setActed] = useState(false);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [confirmDismissed, setConfirmDismissed] = useState(false);
  // Aperçu PDF : index dans la liste plate `paiements` (null = panneau fermé).
  // État séparé → n'affecte pas la sélection ni le reste de la page.
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  const qp = useQuery({
    queryKey: ['paiements', 'validation'],
    queryFn: () => paiementsApi.validation(),
    staleTime: 30_000,
  });
  const qr = useQuery({
    queryKey: ['remboursements', 'validation'],
    queryFn: () => remboursementsApi.validation(),
    staleTime: 30_000,
  });

  const paiements = qp.data?.paiements ?? [];
  const remboursements = qr.data?.remboursements ?? [];
  const isLoading = qp.isLoading || qr.isLoading;
  const error = qp.error || qr.error;
  const totalCount = paiements.length + remboursements.length;
  // Le nom du valideur attribué s'affiche dès qu'on voit les demandes de
  // plusieurs personnes — donc pour tout administrateur depuis le 14/10/2026 :
  // valider à la place de quelqu'un suppose de savoir de qui il s'agit.
  const voitTout = qp.data?.portee === 'toutes';

  const paiementGroups = useMemo(() => {
    const m = new Map<string, Paiement[]>();
    for (const p of paiements) {
      const k = p.type_charge ?? 'Sans type de charge';
      (m.get(k) ?? m.set(k, []).get(k)!).push(p);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [paiements]);

  function toggle(kind: Kind, id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      const k = key(kind, id);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  // Sélection en masse : par bloc (en-tête de groupe) et globale (toolbar).
  const allKeys = useMemo(
    () => [
      ...paiements.map((p) => key('paiement', p.id)),
      ...remboursements.map((r) => key('remboursement', r.id)),
    ],
    [paiements, remboursements],
  );
  const countIn = (keys: string[]) => keys.reduce((n, k) => n + (selected.has(k) ? 1 : 0), 0);
  function setMany(keys: string[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of keys) on ? next.add(k) : next.delete(k);
      return next;
    });
  }
  /** État d'un groupe de clés : tout coché / partiel, + bascule. */
  function groupState(keys: string[]) {
    const sel = countIn(keys);
    const all = keys.length > 0 && sel === keys.length;
    return { all, some: sel > 0 && !all, toggle: () => setMany(keys, !all) };
  }

  const selectedItems = (): Array<{ kind: Kind; id: string }> =>
    [...selected].map((k) => {
      const [kind, id] = k.split(':') as [Kind, string];
      return { kind, id };
    });
  const allItems = (): Array<{ kind: Kind; id: string }> => [
    ...paiements.map((p) => ({ kind: 'paiement' as Kind, id: p.id })),
    ...remboursements.map((r) => ({ kind: 'remboursement' as Kind, id: r.id })),
  ];

  // ── Validation ──────────────────────────────────────────────────────────
  const validateMutation = useMutation({
    mutationFn: async (items: Array<{ kind: Kind; id: string }>) => {
      const pIds = items.filter((i) => i.kind === 'paiement').map((i) => i.id);
      const rIds = items.filter((i) => i.kind === 'remboursement').map((i) => i.id);
      let validated = 0;
      if (pIds.length) validated += (await (pIds.length === 1 ? paiementsApi.validate(pIds[0]!) : paiementsApi.validateAll(pIds))).validated;
      if (rIds.length) validated += (await (rIds.length === 1 ? remboursementsApi.validate(rIds[0]!) : remboursementsApi.validateAll(rIds))).validated;
      return { validated };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['paiements'] });
      qc.invalidateQueries({ queryKey: ['remboursements'] });
      setSelected(new Set());
      setActed(true);
      toast.success(`${r.validated} ligne(s) validée(s).`);
    },
    // `formatApiError` et non `String(err)` : c'est le SEUL écran où s'affiche
    // le refus 403 « lignes attribuées à un autre valideur » opposé depuis le
    // 14/10/2026 à un valideur non administrateur. `String(err)` le préfixait
    // d'un « ApiError: » qui n'apprend rien à qui le lit.
    onError: (err) => toast.error(`Validation échouée : ${formatApiError(err)}`),
  });

  // ── Refus (avec motif) ──────────────────────────────────────────────────
  const rejectMutation = useMutation({
    mutationFn: async ({ items, motif }: { items: Array<{ kind: Kind; id: string }>; motif: string }) => {
      const pIds = items.filter((i) => i.kind === 'paiement').map((i) => i.id);
      const rIds = items.filter((i) => i.kind === 'remboursement').map((i) => i.id);
      let rejected = 0;
      if (pIds.length) rejected += (await paiementsApi.reject(pIds, motif)).rejected;
      if (rIds.length) rejected += (await remboursementsApi.reject(rIds, motif)).rejected;
      return { rejected };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['paiements'] });
      qc.invalidateQueries({ queryKey: ['remboursements'] });
      setSelected(new Set());
      setRejectTarget(null);
      setMotif('');
      setActed(true);
      toast.success(`${r.rejected} ligne(s) refusée(s) : admin notifié.`);
    },
    onError: (err) => toast.error(`Refus échoué : ${formatApiError(err)}`),
  });

  // ── Confirmation finale (notifie l'admin pour l'export) ─────────────────
  const confirmMutation = useMutation({
    mutationFn: async () => {
      const a = await paiementsApi.confirm();
      const b = await remboursementsApi.confirm();
      return { validated: a.validated + b.validated, email_sent: a.email_sent || b.email_sent };
    },
    onSuccess: (r) => {
      setCompletionOpen(false);
      setConfirmDismissed(true);
      let msg = `Confirmé : ${r.validated} ligne(s) signalée(s) à l'admin.`;
      if (!r.email_sent) msg += ' (email non envoyé : SMTP non configuré)';
      toast.success(msg);
    },
    onError: (err) => toast.error(`Confirmation échouée : ${formatApiError(err)}`),
  });

  // Popup de fin : quand tout est traité (liste vide après au moins une action).
  // Réinitialise le « reporté » quand un nouveau lot arrive (liste repeuplée).
  useEffect(() => {
    if (isLoading) return;
    if (totalCount > 0) {
      setConfirmDismissed(false);
      return;
    }
    if (acted && !confirmDismissed) setCompletionOpen(true);
  }, [totalCount, acted, confirmDismissed, isLoading]);

  function validate(items: Array<{ kind: Kind; id: string }>) {
    if (items.length) validateMutation.mutate(items);
  }
  function openReject(items: Array<{ kind: Kind; id: string }>) {
    if (items.length) {
      setMotif('');
      setRejectTarget(items);
    }
  }

  const busy = validateMutation.isPending || rejectMutation.isPending;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">À valider</h1>
        <p className="text-sm text-slate-500 mt-1">
          {voitTout
            ? 'Toutes les demandes en attente, avec le valideur de chacune. '
            : 'Les paiements fournisseurs et remboursements d\'avoirs qui vous sont soumis. '}
          Validez ou refusez chaque ligne (un refus la renvoie dans « À payer »
          avec un motif et notifie l'admin).
        </p>
      </div>

      {isLoading ? (
        <p className="text-slate-500">Chargement…</p>
      ) : error ? (
        <p className="text-[#C0423F]">Erreur : {formatApiError(error)}</p>
      ) : totalCount === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500 space-y-3">
          <p>Rien en attente de validation.</p>
          {acted && (
            <button
              type="button"
              onClick={() => confirmMutation.mutate()}
              disabled={confirmMutation.isPending}
              className="px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {confirmMutation.isPending && <Spinner />}
              Confirmer et notifier l'admin
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Toolbar : tout sélectionner */}
          {(() => {
            const g = groupState(allKeys);
            return (
              <div className="flex items-center gap-2 px-1">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <TriCheckbox checked={g.all} indeterminate={g.some} onChange={g.toggle} title="Tout sélectionner" />
                  Tout sélectionner ({totalCount})
                </label>
              </div>
            );
          })()}

          {/* Paiements fournisseurs */}
          {paiementGroups.map(([charge, items]) => {
            const total = items.reduce((s, p) => s + p.montant, 0);
            const g = groupState(items.map((p) => key('paiement', p.id)));
            return (
              <div key={charge} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between bg-slate-50 px-4 py-2 border-b border-slate-200">
                  <label className="flex items-center gap-2 font-medium cursor-pointer">
                    <TriCheckbox checked={g.all} indeterminate={g.some} onChange={g.toggle} title="Sélectionner ce bloc" />
                    {charge}
                  </label>
                  <span className="text-sm text-slate-600">Total : {eur(total)}</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-slate-500 text-left text-xs uppercase">
                    <tr>
                      <th className="px-3 py-2 w-8"></th>
                      <th className="px-3 py-2 font-medium">Fournisseur</th>
                      <th className="px-3 py-2 font-medium">N° facture</th>
                      <th className="px-3 py-2 font-medium text-right">Montant</th>
                      <th className="px-3 py-2 font-medium">Banque</th>
                      <th className="px-3 py-2 font-medium">IBAN</th>
                      <th className="px-3 py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((p) => (
                      <tr key={p.id}>
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={selected.has(key('paiement', p.id))} onChange={() => toggle('paiement', p.id)} />
                        </td>
                        <td className="px-3 py-2 font-medium">
                          {p.vendor_name}
                          {voitTout && p.valideur_nom && <BadgeValideur nom={p.valideur_nom} />}
                          {/* Contexte : ligne déjà refusée (avec la réponse de
                              l'admin) ou ligne hors synchro. Sans ça, une
                              facture qui revient ou qui sort de nulle part est
                              illisible au moment de valider. */}
                          {p.rejet_motif && (
                            <MotifEtReponse motif={p.rejet_motif} reponse={p.reponse_admin} />
                          )}
                          {p.origine && p.origine !== 'zoho' && p.origine_motif && (
                            <div className="mt-1 text-xs font-normal text-slate-600">
                              {p.origine === 'virement_interne'
                                ? '⇄ '
                                : `${p.origine === 'reprise' ? '↻ Solde dû' : '✎ Saisie manuelle'} : `}
                              {p.origine_motif}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{p.bill_number}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{eur(p.montant)}</td>
                        <td className="px-3 py-2">{p.banque ?? '-'}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-500">{fmtIban(p.beneficiaire?.iban)}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5">
                            {p.origine !== 'virement_interne' && (
                              <button
                                type="button"
                                onClick={() => setPreviewIndex(paiements.findIndex((x) => x.id === p.id))}
                                title="Voir la facture"
                                className="px-2.5 py-1 rounded-md text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                              >
                                👁 Voir
                              </button>
                            )}
                            <RowActions
                              onValidate={() => validate([{ kind: 'paiement', id: p.id }])}
                              onReject={() => openReject([{ kind: 'paiement', id: p.id }])}
                              disabled={busy}
                            />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}

          {/* Remboursements d'avoirs */}
          {remboursements.length > 0 && (() => {
            const g = groupState(remboursements.map((r) => key('remboursement', r.id)));
            return (
            <div className="bg-white rounded-lg border border-purple-200 overflow-hidden">
              <div className="flex items-center justify-between bg-purple-50 px-4 py-2 border-b border-purple-200">
                <label className="flex items-center gap-2 font-medium text-purple-900 cursor-pointer">
                  <TriCheckbox checked={g.all} indeterminate={g.some} onChange={g.toggle} title="Sélectionner ce bloc" />
                  Remboursements d'avoirs (clients)
                </label>
                <span className="text-sm text-purple-700">
                  Total : {eur(remboursements.reduce((s, r) => s + r.montant, 0))}
                </span>
              </div>
              <table className="w-full text-sm">
                <thead className="text-slate-500 text-left text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2 font-medium">Client</th>
                    <th className="px-3 py-2 font-medium">N° Avoir</th>
                    <th className="px-3 py-2 font-medium text-right">Montant</th>
                    <th className="px-3 py-2 font-medium">Banque</th>
                    <th className="px-3 py-2 font-medium">IBAN</th>
                    <th className="px-3 py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {remboursements.map((r) => (
                    <tr key={r.id}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={selected.has(key('remboursement', r.id))} onChange={() => toggle('remboursement', r.id)} />
                      </td>
                      <td className="px-3 py-2 font-medium">
                        {r.customer_name}
                        {voitTout && r.valideur_nom && <BadgeValideur nom={r.valideur_nom} />}
                        {/* Contexte : avoir déjà refusé, avec la réponse de
                            l'admin. Le valideur doit revoir sa propre question
                            avant de statuer une seconde fois. */}
                        {r.rejet_motif && (
                          <MotifEtReponse motif={r.rejet_motif} reponse={r.reponse_admin} />
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.credit_note_number}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{eur(r.montant)}</td>
                      <td className="px-3 py-2">{r.banque ?? '-'}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{fmtIban(r.beneficiaire?.iban)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <RowActions
                          onValidate={() => validate([{ kind: 'remboursement', id: r.id }])}
                          onReject={() => openReject([{ kind: 'remboursement', id: r.id }])}
                          disabled={busy}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            );
          })()}

          {/* Barre d'actions en masse (étape de traitement : valider OU refuser) */}
          <div className="sticky bottom-0 bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3 shadow-sm">
            <span className="text-sm text-slate-600">{selected.size} sélectionné(s)</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => validate(selected.size ? selectedItems() : allItems())}
                disabled={busy}
                className="px-3 py-2 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {validateMutation.isPending && <Spinner />}
                {selected.size ? `Valider la sélection (${selected.size})` : 'Tout valider'}
              </button>
              <button
                type="button"
                onClick={() => openReject(selected.size ? selectedItems() : allItems())}
                disabled={busy}
                className="px-3 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {selected.size ? `Refuser la sélection (${selected.size})` : 'Tout refuser'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale de refus : motif + notification admin */}
      {rejectTarget && (
        <Modal onClose={() => !rejectMutation.isPending && setRejectTarget(null)}>
          <h2 className="text-lg font-semibold">Refuser {rejectTarget.length} ligne(s)</h2>
          <p className="text-sm text-slate-500 mt-1">
            Indiquez la ou les raisons du refus. Les lignes repasseront dans
            « À payer » et l'admin sera notifié.
          </p>
          <textarea
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            rows={4}
            autoFocus
            placeholder="Ex. IBAN à vérifier, montant erroné, facture en double…"
            className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setRejectTarget(null)}
              disabled={rejectMutation.isPending}
              className="px-3 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={() => rejectMutation.mutate({ items: rejectTarget, motif: motif.trim() })}
              disabled={rejectMutation.isPending || motif.trim().length === 0}
              className="px-3 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {rejectMutation.isPending && <Spinner />}
              Refuser et notifier l'admin
            </button>
          </div>
        </Modal>
      )}

      {/* Popup de fin : tout est traité → confirmer et notifier l'admin */}
      {completionOpen && (
        <Modal onClose={() => { setCompletionOpen(false); setConfirmDismissed(true); }}>
          <h2 className="text-lg font-semibold">Toutes les lignes sont traitées ✓</h2>
          <p className="text-sm text-slate-600 mt-2">
            Vous avez validé et/ou refusé toutes les lignes en attente. Tout
            est-il OK ? Confirmez pour signaler à l'admin que la validation est
            terminée : il pourra alors générer les fichiers de virement (SEPA).
          </p>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setCompletionOpen(false); setConfirmDismissed(true); }}
              disabled={confirmMutation.isPending}
              className="px-3 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              Plus tard
            </button>
            <button
              type="button"
              onClick={() => confirmMutation.mutate()}
              disabled={confirmMutation.isPending}
              className="px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {confirmMutation.isPending && <Spinner />}
              Confirmer et notifier l'admin
            </button>
          </div>
        </Modal>
      )}

      {/* Panneau latéral : aperçu PDF de la facture (paiements uniquement). */}
      {previewIndex != null && paiements[previewIndex] && (
        <PdfPreviewDrawer
          paiement={paiements[previewIndex]!}
          onClose={() => setPreviewIndex(null)}
          onPrev={() => setPreviewIndex((i) => (i != null && i > 0 ? i - 1 : i))}
          onNext={() =>
            setPreviewIndex((i) => (i != null && i < paiements.length - 1 ? i + 1 : i))
          }
          hasPrev={previewIndex > 0}
          hasNext={previewIndex < paiements.length - 1}
        />
      )}
    </div>
  );
}

/** Boutons Valider (vert) + Refuser (rouge) d'une ligne. */
function RowActions({
  onValidate,
  onReject,
  disabled,
}: {
  onValidate: () => void;
  onReject: () => void;
  disabled: boolean;
}) {
  return (
    <span className="inline-flex gap-1.5">
      <button
        type="button"
        onClick={onValidate}
        disabled={disabled}
        className="px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
      >
        Valider
      </button>
      <button
        type="button"
        onClick={onReject}
        disabled={disabled}
        className="px-2.5 py-1 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-50"
      >
        Refuser
      </button>
    </span>
  );
}

/** Modale centrée générique (overlay + carte). */
function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl border border-slate-200 w-full max-w-md p-5">
        {children}
      </div>
    </div>
  );
}
